import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Gift, ArrowUp, ArrowDown, Calendar } from "@phosphor-icons/react";
import api from "@/lib/api";
import { pts, initials, fmtDate, fmtDateTime, tierColor } from "@/lib/format";

export default function BusinessProfile() {
  const { id } = useParams();
  const nav = useNavigate();
  const [d, setD] = useState(null);

  useEffect(() => {
    api.get(`/customer/businesses/${id}`).then((r) => setD(r.data)).catch(() => nav("/app/businesses"));
  }, [id, nav]);

  if (!d) return <div className="pt-10 text-center text-muted-foreground">Loading…</div>;

  return (
    <div className="pt-2 space-y-5">
      <button onClick={() => nav(-1)} data-testid="profile-back" className="text-muted-foreground flex items-center gap-1 text-sm"><ArrowLeft size={16} /> Back</button>

      <div className="card-surface p-6 relative overflow-hidden">
        <div className="flex items-center gap-4">
          <div className="h-14 w-14 rounded-2xl bg-primary/15 flex items-center justify-center overflow-hidden">
            {d.business.logo ? <img src={d.business.logo} alt="" className="h-full w-full object-cover" /> : <span className="font-display font-bold text-primary text-lg">{initials(d.business.name)}</span>}
          </div>
          <div>
            <h1 className="font-display font-bold text-xl">{d.business.name}</h1>
            <p className="text-xs text-muted-foreground">{d.business.category} · {d.business.city}</p>
          </div>
        </div>
        <div className="mt-6 flex items-end justify-between">
          <div>
            <p className="text-xs text-muted-foreground">Current balance</p>
            <p className="font-display font-extrabold text-4xl text-primary" data-testid="profile-points">{pts(d.points)}<span className="text-lg text-muted-foreground ml-1">pts</span></p>
          </div>
          <span className={`font-display font-bold ${tierColor(d.tier)}`} data-testid="profile-tier">{d.tier} tier</span>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="card-surface p-4"><ArrowUp size={18} className="text-primary mb-1" /><p className="font-display font-bold text-lg">{pts(d.earned)}</p><p className="text-xs text-muted-foreground">Points earned</p></div>
        <div className="card-surface p-4"><ArrowDown size={18} className="text-destructive mb-1" /><p className="font-display font-bold text-lg">{pts(d.redeemed)}</p><p className="text-xs text-muted-foreground">Points redeemed</p></div>
      </div>

      <div>
        <h2 className="font-display font-semibold mb-3 flex items-center gap-2"><Gift size={18} className="text-primary" /> Available rewards</h2>
        <div className="space-y-2">
          {d.rewards.length === 0 && <p className="text-sm text-muted-foreground">No rewards yet.</p>}
          {d.rewards.map((r) => (
            <div key={r.id} data-testid="profile-reward" className={`card-surface p-4 flex items-center justify-between ${r.eligible ? "border-primary/40" : ""}`}>
              <div>
                <p className="font-medium">{r.name}</p>
                <p className="text-xs text-muted-foreground">{r.description}</p>
              </div>
              <div className="text-right">
                <p className="font-display font-bold text-primary">{pts(r.points_required)}</p>
                <p className={`text-[10px] ${r.eligible ? "text-primary" : "text-muted-foreground"}`}>{r.eligible ? "Eligible" : "Keep earning"}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div>
        <h2 className="font-display font-semibold mb-3 flex items-center gap-2"><Calendar size={18} className="text-muted-foreground" /> Recent transactions</h2>
        <p className="text-xs text-muted-foreground mb-2">Joined {fmtDate(d.joined_at)} · {d.visits} visits</p>
        <div className="space-y-2">
          {d.transactions.map((t) => (
            <div key={t.id} className="card-surface p-3.5 flex items-center justify-between" data-testid="profile-txn">
              <div>
                <p className="text-sm font-medium capitalize">{t.type === "redemption" ? t.reward_name || "Redemption" : t.type}</p>
                <p className="text-xs text-muted-foreground">{fmtDateTime(t.created_at)}</p>
              </div>
              <span className={`font-display font-bold ${t.type === "redemption" ? "text-destructive" : "text-primary"}`}>
                {t.type === "redemption" ? `-${pts(t.points_spent)}` : `+${pts(t.points_awarded)}`}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
